import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Users, LogOut, ArrowRight, Copy, ShieldPlus } from "lucide-react";
import GaaBallIcon from "../components/GaaBall";

export default function Teams() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeam, setNewTeam] = useState({ name: "", sport: "football", gender: "boys" });
  const [joinCode, setJoinCode] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);

  const load = () => {
    api.get("/teams").then((r) => setTeams(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!newTeam.name.trim()) return;
    try {
      const { data } = await api.post("/teams", newTeam);
      toast.success(`Team created · code ${data.code}`);
      setOpenCreate(false);
      setNewTeam({ name: "", sport: "football", gender: "boys" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const join = async () => {
    try {
      const { data } = await api.post("/teams/join", { code: joinCode });
      toast.success(`Joined ${data.name}`);
      setOpenJoin(false);
      setJoinCode("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-30 bg-background/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <GaaBallIcon size={19} />
            </div>
            <span className="font-display font-extrabold tracking-tight">GaaPlay</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Your teams</h1>
            <p className="text-muted-foreground text-sm mt-1">Select a team to open its playbook, or join a squad with a code.</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={openJoin} onOpenChange={setOpenJoin}>
              <DialogTrigger asChild>
                <Button data-testid="join-team-btn" variant="secondary"><ShieldPlus className="h-4 w-4 mr-1" />Join with code</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="font-display">Join a team</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">Enter the code your head coach shared. All coaches on a team share the same playbook.</p>
                <Input data-testid="join-code-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. DEMO24" className="font-mono2 tracking-widest" />
                <Button data-testid="join-confirm-btn" onClick={join}>Join team</Button>
              </DialogContent>
            </Dialog>

            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-team-btn"><Plus className="h-4 w-4 mr-1" />New team</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader><DialogTitle className="font-display">Create a team</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Team name</Label>
                    <Input data-testid="team-name-input" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} placeholder="My Club Seniors" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sport</Label>
                    <div className="flex gap-2 mt-1.5">
                      {["football", "hurling"].map((s) => (
                        <button key={s} onClick={() => setNewTeam({ ...newTeam, sport: s })}
                          className={`flex-1 py-2 rounded-md text-sm capitalize border ${newTeam.sport === s ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Team</Label>
                    <div className="flex gap-2 mt-1.5">
                      {[["boys", "Boys / Men"], ["girls", "Girls / Ladies"]].map(([g, label]) => (
                        <button key={g} data-testid={`team-gender-${g}`} onClick={() => setNewTeam({ ...newTeam, gender: g })}
                          className={`flex-1 py-2 rounded-md text-sm border ${newTeam.gender === g ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button data-testid="team-create-confirm" onClick={create} className="w-full">Create & generate share code</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm font-mono2 animate-pulse">Loading teams…</div>
        ) : teams.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-xl">
            <Users className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No teams yet. Create one or join with a code.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((t) => (
              <div key={t.id} data-testid={`team-card-${t.id}`}
                className="group relative rounded-xl border border-border bg-card p-5 hover:border-primary/60 transition-colors cursor-pointer"
                onClick={() => nav(`/team/${t.id}`)}>
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center font-display font-extrabold text-lg" style={{ background: t.colorPrimary + "22", color: t.colorPrimary }}>
                    {t.name.slice(0, 2).toUpperCase()}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">{t.sport}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5" data-testid={`team-gender-badge-${t.id}`}>{t.gender === "girls" ? "Girls" : "Boys"}</span>
                </div>
                <h3 className="font-display font-bold text-lg mt-4">{t.name}</h3>
                <div className="flex items-center justify-between mt-3">
                  <button data-testid={`copy-code-${t.id}`} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(t.code); toast.success("Team code copied"); }}
                    className="flex items-center gap-1.5 font-mono2 text-xs text-primary hover:opacity-80">
                    <Copy className="h-3 w-3" />{t.code}
                  </button>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
